import { View, Text, styled, ScrollView } from 'tamagui'
import { Pin, X } from '@tamagui/lucide-icons'
import { useAppStore } from '@/store'
import { messages, users } from '@/mocks'
import { Avatar } from '../../common'

const Panel = styled(View, {
  width: 340,
  backgroundColor: '$surfaceRaised',
  borderLeftWidth: 1,
  borderLeftColor: '$borderDefault',
  flexDirection: 'column',
  flexShrink: 0,
})

const PanelHeader = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const CloseButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const PinnedItem = styled(View, {
  padding: 12,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  gap: 8,
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const UnpinButton = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  height: 24,
  paddingHorizontal: 8,
  borderRadius: '$borderRadiusSm',
  cursor: 'pointer',
  borderWidth: 1,
  borderColor: '$borderDefault',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const EmptyState = styled(View, {
  flex: 1,
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: 32,
})

function formatTime(timestamp: number): string {
  const date = new Date(timestamp)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export function PinnedMessages() {
  const { activeChannelId, togglePinned } = useAppStore()

  const channelMessages = activeChannelId ? messages[activeChannelId] || [] : []
  const pinnedMessages = channelMessages.filter(m => m.isPinned === true)

  return (
    <Panel>
      <PanelHeader>
        <View flexDirection="row" alignItems="center" gap={8}>
          <Pin size={16} color="$accent" />
          <Text fontFamily="$fontBody" fontWeight="600" fontSize={14} color="$surfaceForeground">
            Pinned Messages
          </Text>
        </View>
        <CloseButton onPress={togglePinned}>
          <X size={16} color="$sidebarMuted" />
        </CloseButton>
      </PanelHeader>

      <ScrollView flex={1} showsVerticalScrollIndicator={false}>
        {pinnedMessages.length === 0 ? (
          <EmptyState>
            <Pin size={32} color="$color8" />
            <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted" textAlign="center">
              No pinned messages yet
            </Text>
            <Text fontFamily="$fontBody" fontSize={12} color="$color8" textAlign="center">
              Right-click a message and select "Pin Message" to pin it here.
            </Text>
          </EmptyState>
        ) : (
          pinnedMessages.map((msg) => {
            const user = users[msg.authorId]
            if (!user) return null
            return (
              <PinnedItem key={msg.id}>
                <View flexDirection="row" alignItems="center" gap={8}>
                  <Avatar name={user.displayName} color={user.roleColor || '#8B95A8'} size="sm" showStatus={false} />
                  <View flex={1}>
                    <View flexDirection="row" alignItems="baseline" gap={6}>
                      <Text fontFamily="$fontBody" fontSize={13} fontWeight="600" color={user.roleColor || '$surfaceForeground'}>
                        {user.displayName}
                      </Text>
                      <Text fontFamily="$fontMono" fontSize={10} color="$color8">
                        {formatTime(msg.timestamp)}
                      </Text>
                    </View>
                  </View>
                </View>
                <Text fontFamily="$fontMono" fontSize={13} color="$surfaceForeground" numberOfLines={3}>
                  {msg.content}
                </Text>
                <UnpinButton>
                  <Pin size={10} color="$sidebarMuted" />
                  <Text fontFamily="$fontBody" fontSize={11} color="$sidebarMuted">Unpin</Text>
                </UnpinButton>
              </PinnedItem>
            )
          })
        )}
      </ScrollView>
    </Panel>
  )
}
